import { checkInAt, extendLabel, pauseAfterUnanswered } from './timer';
import t from '../i18n/t';

/**
 * The chime and desktop notice for a timer while Shovel is out of view (timer-alerts spec, Petr 2026-10-31): at the
 * plan's end and at each "Still studying?" past it, the moments the top bar's pill asks. Never before the plan's end,
 * never for a paused timer, and none at the check-in where the timer pauses itself (it pauses there instead).
 */

/** Later than this, a moment is late (a laptop back from sleep): a quiet notice, no chime. */
export const LATE_MS = 60 * 1000;

export type AlertMoment = {
  /** 0 is the plan's end, 1 the first "Still studying?" a lap later (utils/timer.ts checkInAt). */
  checkIn: number;
  /** When, on the wall clock. */
  at: number;
  /** The time the timer has counted then. */
  usedSeconds: number;
};

export type AlertTimer = {
  timePassedInSeconds: number;
  /** When the current run started; none while paused. */
  startedInUtc: Date | string | null | undefined;
  planSeconds: number;
  /** The last check-in answered (utils/timerCheckIns.ts), 0 for the plan's end. */
  answered: number;
};

const usedAt = (timer: AlertTimer, now: number) =>
  (timer.timePassedInSeconds || 0) + (now - new Date(timer.startedInUtc as any).getTime()) / 1000;

/**
 * The moments still to come for a running timer, from the next one up to the last before it pauses itself. `alerted` is
 * the last check-in already alerted in this browser (-1 for none); one already passed but not alerted is included, for
 * the late rule.
 */
export const alertMoments = (timer: AlertTimer, alerted: number, now: number): AlertMoment[] => {
  const { planSeconds, startedInUtc } = timer;
  if (!startedInUtc || !(planSeconds > 0)) return [];
  const startedAt = new Date(startedInUtc as any).getTime();
  const base = timer.timePassedInSeconds || 0;
  // The check-in two after the last answered is where it pauses itself: the one before it is the last to ask.
  const last = pauseAfterUnanswered(timer.answered, planSeconds) / planSeconds - 2;
  const moments: AlertMoment[] = [];
  for (let checkIn = Math.max(0, alerted + 1); checkIn <= last; checkIn++) {
    const usedSeconds = (checkIn + 1) * planSeconds;
    moments.push({ checkIn, usedSeconds, at: startedAt + (usedSeconds - base) * 1000 });
  }
  return moments.filter(moment => moment.at > now - 24 * 60 * 60 * 1000);
};

/**
 * The last check-in alerted, as far as it still holds: time taken back (typed lower, Reset) below a moment already
 * alerted brings that moment back.
 */
export const stillAlerted = (alerted: number, timer: AlertTimer, now: number) => {
  if (alerted < 0 || !(timer.planSeconds > 0)) return -1;
  const used = timer.startedInUtc ? usedAt(timer, now) : timer.timePassedInSeconds || 0;
  return Math.min(alerted, checkInAt(used, timer.planSeconds));
};

/**
 * What to do when a moment comes: `alert` on time, `late` (a notice without the chime), or `skip`. Late ones skip
 * when a later moment has also passed (only the latest is told) or the timer has paused itself meanwhile (the pill
 * says so when the student looks).
 */
export const momentAction = (moment: AlertMoment, timer: AlertTimer, now: number): 'alert' | 'late' | 'skip' => {
  if (now - moment.at <= LATE_MS) return 'alert';
  const used = usedAt(timer, now);
  if (used >= pauseAfterUnanswered(timer.answered, timer.planSeconds)) return 'skip';
  if (checkInAt(used, timer.planSeconds) > moment.checkIn) return 'skip';
  return 'late';
};

const agoLabel = (minutes: number) => (minutes < 60 ? `${minutes} min` : extendLabel(minutes));

export type AlertWords = { title: string; body: string };

/**
 * The notice's words, as few as can be read before it slides away (Petr, 2026-10-31): "Time's up · Log 1h" over the
 * session's name.
 */
export const alertWords = (moment: AlertMoment, name: string | undefined, lateMs = 0): AlertWords => {
  const used = extendLabel(Math.floor(moment.usedSeconds / 60));
  const body = name || '';
  if (moment.checkIn >= 1) return { title: t.TIMER_ALERT_CHECK_IN(used), body };
  const late = Math.floor(lateMs / 60000);
  return {
    title: lateMs > LATE_MS ? t.TIMER_ALERT_TIMES_UP_LATE(agoLabel(late), used) : t.TIMER_ALERT_TIMES_UP(used),
    body
  };
};
